import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnInit,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {onEach} from '../../../utils/observable-utils';
import {ResponsiveService} from "../../../responsive/responsive.service";
import {PaymentModel} from "../../payment.model";
import {CardPaymentSubmissionResult, PaymentAuthorisationType, PaymentCard} from '../../payment';
import {FieldComponent} from '../../../inputs/field/field.component';
import {FieldPairComponent} from '../../../inputs/form/field-pair/field-pair.component';
import {SelectorControlComponent} from '../../../inputs/selector-control/selector-control.component';
import {ConfigurationModel} from '../../../configuration/configuration.model';

declare type StripeFieldStatus = {
    brand: string;
    complete: boolean;
    elementType: string;
    empty: boolean;
    error: {
        code: string;
        type: string;
        message: string;
    };
    value: string;
};

@Component({
    selector: "woa-stripe-manual-payment-card-panel",
    templateUrl: "./stripe-manual-payment-card-panel.component.html",
    styleUrl: "./stripe-manual-payment-card-panel.component.scss",
    providers: [{provide: PaymentCard, useExisting: StripeManualPaymentCardPanelComponent}],
    imports: [
        FieldComponent,
        FieldPairComponent,
        SelectorControlComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StripeManualPaymentCardPanelComponent extends PaymentCard implements OnInit, AfterViewInit {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private element: ElementRef = inject(ElementRef);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private responsiveService: ResponsiveService = inject(ResponsiveService);
    private paymentModel: PaymentModel = inject(PaymentModel);

    public readonly disabled: InputSignal<boolean> = input<boolean>(false);

    cardNumberField: Signal<ElementRef> = viewChild.required("cardNumber");
    cardExpiryField: Signal<ElementRef> = viewChild.required("cardExpiry");
    cardCvcField: Signal<ElementRef> = viewChild.required("cardCvc");

    private readonly submitted: WritableSignal<boolean> = signal<boolean>(false);
    private readonly cardNumberStatus: WritableSignal<StripeFieldStatus> = signal<StripeFieldStatus>(undefined);
    private readonly cardExpiryStatus: WritableSignal<StripeFieldStatus> = signal<StripeFieldStatus>(undefined);
    private readonly cardCvcStatus: WritableSignal<StripeFieldStatus> = signal<StripeFieldStatus>(undefined);

    public readonly cardNumberError: Signal<string> = computed<string>(() =>
        this.cardNumberStatus() && this.cardNumberStatus().error ? "'" + this.cardNumberStatus().error.message + "'"
            : (this.cardNumberStatus() && this.cardNumberStatus().empty) || (this.submitted() && !this.cardNumberStatus()) ? "woa.validation.common.required"
                : undefined);

    public readonly cardExpiryError: Signal<string> = computed<string>(() =>
        this.cardExpiryStatus() && this.cardExpiryStatus().error ? "'" + this.cardExpiryStatus().error.message + "'"
            : (this.cardExpiryStatus() && this.cardExpiryStatus().empty) || (this.submitted() && !this.cardExpiryStatus()) ? "woa.validation.common.required"
                : undefined);

    public readonly cardCvcError: Signal<string> = computed<string>(() =>
        this.cardCvcStatus() && this.cardCvcStatus().error ? "'" + this.cardCvcStatus().error.message + "'"
            : (this.cardCvcStatus() && this.cardCvcStatus().empty) || (this.submitted() && !this.cardCvcStatus()) ? "woa.validation.common.required"
                : undefined);

    public readonly showSavePaymentDetailsOption: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.isConsumerActor());

    public savePaymentDetails: boolean = false;
    private stripe: any;
    private cardNumberElement: any;
    private cardExpiryElement: any;
    private cardCvcElement: any;

    constructor() {
        super();
        this.savePaymentDetails = this.responsiveService.isMobile() || this.configurationModel.devOrDemoMode();

        effect(() => {
            if (this.configurationModel.app()) {
                this.styleCardElements();
            }
        });
    }

    ngOnInit(): void {
        onEach(this.paymentModel.paymentDetailsApplied$, () => this.setup(), this.destroyRef);
    }

    ngAfterViewInit(): void {
        this.setup();
    }

    public canSavePaymentDetails(): boolean {
        return this.showSavePaymentDetailsOption() && this.savePaymentDetails;
    }

    public isValid(): boolean {
        this.submitted.set(true);
        return !this.cardNumberError() && !this.cardExpiryError() && !this.cardCvcError();
    }

    public markAsUnsubmitted(): void {
        this.submitted.set(false);
    }

    public async submit(): Promise<CardPaymentSubmissionResult> {
        const result: any = await this.stripe.createPaymentMethod({type: "card", card: this.cardNumberElement});
        if (result.error) {
            throw result.error.message;
        }
        return new CardPaymentSubmissionResult(PaymentAuthorisationType.STRIPE_PAYMENT_METHOD, result.paymentMethod.id, result.paymentMethod.card.brand, undefined, result.paymentMethod.card.last4);
    }

    private setup(): void {
        if (!this.stripe && this.paymentModel.paymentPlatformPublicKey() && this.cardNumberField() && this.cardExpiryField() && this.cardCvcField()) {
            // noinspection JSUnresolvedReference
            this.stripe = new (<any>window).Stripe(this.paymentModel.paymentPlatformPublicKey());
            const elements = this.stripe.elements();
            this.cardNumberElement = elements.create("cardNumber", {showIcon: true, placeholder: ""});
            this.cardExpiryElement = elements.create("cardExpiry", {placeholder: ""});
            this.cardCvcElement = elements.create("cardCvc", {placeholder: ""});
            setTimeout(() => this.styleCardElements());
            this.cardNumberElement.on("change", (status: StripeFieldStatus) => {
                this.cardNumberStatus.set(status);
                if (status.complete) {
                    this.cardExpiryElement.focus();
                }
            });
            this.cardExpiryElement.on("change", (status: StripeFieldStatus) => {
                this.cardExpiryStatus.set(status);
                if (status.complete) {
                    this.cardCvcElement.focus();
                }
            });
            this.cardCvcElement.on("change", (status: StripeFieldStatus) => {
                this.cardCvcStatus.set(status);
            });
            setTimeout(() => {
                this.cardNumberElement.mount(this.cardNumberField().nativeElement);
                this.cardExpiryElement.mount(this.cardExpiryField().nativeElement);
                this.cardCvcElement.mount(this.cardCvcField().nativeElement);
            });
        }
    }

    private styleCardElements(): void {
        if (this.cardNumberElement) {
            const color: string = getComputedStyle(this.element.nativeElement).getPropertyValue("--theme-color-pair-foreground");
            if (!color) {
                setTimeout(() => this.styleCardElements(), 100);
                return;
            }
            const font: string = getComputedStyle(this.element.nativeElement).getPropertyValue("--woa-theme-font-common");
            [this.cardNumberElement, this.cardExpiryElement, this.cardCvcElement].forEach((cardElement: any) => {
                cardElement.update({
                    style: {
                        base: {
                            fontFamily: font,
                            fontSize: "16px",
                            lineHeight: "19px",
                            iconColor: "rgb(" + color + ")",
                            color: "rgb(" + color + ")"
                        },
                        invalid: {
                            color: "rgb(" + color + ")",
                        }
                    }
                });
            });
        }
    }
}
